import type { loginSchema } from './schemas/login-schema';
import type { signupSchema } from './schemas/signup-schema';
import type { AuthUserInfo } from './NewAuthProvider';
import type { SignInOutput, SignInWithRedirectInput, SignUpOutput } from 'aws-amplify/auth';
import type z from 'zod';
import { getEnvConfig } from '../../envConfig';

import {
  AuthError,
  confirmResetPassword,
  confirmSignUp,
  fetchAuthSession,
  fetchMFAPreference,
  getCurrentUser as getCurrentUserAmplify,
  resendSignUpCode as amplifyResendSignUpCode,
  resetPassword,
  setUpTOTP as setUpTOTPAmplify,
  signIn,
  signInWithRedirect,
  signOut,
  signUp,
  updateMFAPreference,
  updatePassword as updatePasswordAmplify,
  verifyTOTPSetup as verifyTOTPSetupAmplify,
} from 'aws-amplify/auth';
import type {
  SetUpTOTPOutput,
  UpdateMFAPreferenceInput,
  FetchMFAPreferenceOutput,
  UpdatePasswordInput,
} from 'aws-amplify/auth';

/**
 * Determine if a user is logged into the current session
 * @returns Authenticated predicate
 */
export async function isAuthenticated() {
  const user = await getCurrentUser();

  return !!user;
}

/**
 * Get the current logged in user
 * @returns User if logged in
 */
export async function getCurrentUser() {
  try {
    const user = await getCurrentUserAmplify();

    return user;
  } catch (error) {
    console.error('Error getting current user', error);
    return undefined;
  }
}

export async function getUserInfo(): Promise<AuthUserInfo | undefined> {
  try {
    const { idToken } = (await fetchAuthSession()).tokens ?? {};
    return idToken?.payload as AuthUserInfo;
  } catch (_) {
    return undefined;
  }
}

/**
 * Get the current access token
 * @returns Access token if set
 */
export async function getToken() {
  try {
    const { accessToken } = (await fetchAuthSession()).tokens ?? {};
    return accessToken?.toString();
  } catch (_) {
    return undefined;
  }
}

/**
 * Create a new user in Cognito with a UUID
 * @param data User details
 * @param data.email User's email
 * @param data.password User's password
 * @returns MFA verification function
 */
export async function authSignUp(data: z.infer<typeof signupSchema>): Promise<SignUpOutput> {
  try {
    return signUp({
      username: data.email,
      password: data.password,
      options: {
        userAttributes: {
          email: data.email.toLowerCase(),
        },
      },
    });
  } catch (error) {
    if (error instanceof AuthError) {
      if (error.name === 'UsernameExistsException') {
        throw new Error('User already exists', { cause: 'The email you entered is already in use.' });
      }
    }
    throw error;
  }
}

/**
 * Sign a user in using an email and password
 * @param data Email and password
 * @param data.email User's email
 * @param data.password User's password
 * @returns MFA verification function
 */
export async function authSignIn({ email, password }: z.infer<typeof loginSchema>): Promise<SignInOutput> {
  try {
    const res = await signIn({
      username: email.toLowerCase(),
      password,
    });

    return res;
  } catch (error) {
    if (error instanceof AuthError) {
      if (error.name === 'NotAuthorizedException' || error.name === 'UserNotFoundException') {
        throw new Error('Invalid credentials', { cause: 'The email or password you entered is incorrect.' });
      }
    }
    throw error;
  }
}

/**
 * Sign a user in using an external provider
 * @param provider Google | Facebook etc..
 * @returns res
 */
export async function signInWithProvider(provider: SignInWithRedirectInput['provider'], redirectUrl?: string) {
  const res = await signInWithRedirect({ provider, customState: redirectUrl || '' });
  return res;
}

/**
 * Sign out the current user session
 * @returns Response
 */
export async function authSignOut() {
  return signOut();
}

/**
 * Confirm a sign up verification code
 * @param email User's email
 * @param code Verification code from user
 * @returns Response
 */
export async function confirmSignUpCode(email: string, code: string) {
  try {
    return confirmSignUp({
      username: email.toLowerCase(),
      confirmationCode: code,
    });
  } catch (error) {
    if (error instanceof AuthError) {
      if (error.name === 'ExpiredCodeException') {
        throw new Error('The verification code has expired. Please request a new code.', {
          cause: 'The verification code has expired. Please request a new code.',
        });
      }
      if (error.name === 'CodeMismatchException') {
        throw new Error('The verification code is incorrect. Please try again.', {
          cause: 'The verification code is incorrect. Please try again.',
        });
      }
    }
    throw error;
  }
}

/**
 * Resend a sign up/in verification code
 * @param email User's email
 * @returns Response
 */
export async function resendSignUpCode(email: string) {
  return amplifyResendSignUpCode({ username: email.toLowerCase() });
}

/**
 * Send confirmation code to user's email
 * @param email User's email
 * @returns Response
 */
export async function forgotPassword(email: string) {
  return resetPassword({ username: email.toLowerCase() });
}

/**
 * Collect confirmation code and new password, then update password if all correct
 * @param email User's email
 * @param code User's received code
 * @param newPassword User's new password
 * @returns Response
 */
export async function forgotPasswordConfirm(email: string, code: string, newPassword: string) {
  return confirmResetPassword({
    username: email.toLowerCase(),
    confirmationCode: code,
    newPassword,
  });
}

/**
 * Redirect to external auth service
 * @returns void
 */
export function redirectToExternalAuth(): void {
  // Import getEnvConfig here to avoid circular dependency

  const config = getEnvConfig();

  const currentUrl = window.location.href;
  const authUrl = `${config.externalAuthUrl}/signin?prev=${btoa(currentUrl)}`;

  window.location.href = authUrl;
}

// MFA Types
export type MFA_METHOD_ID = 'sms' | 'totp' | 'email';
export type MFAPreferenceValue = 'ENABLED' | 'PREFERRED' | 'DISABLED';

/**
 * Get current user's MFA preference and enabled methods
 * @returns MFA preference settings
 */
export async function getMFAPreference(): Promise<FetchMFAPreferenceOutput> {
  return await fetchMFAPreference();
}

/**
 * Update user's MFA preference using Cognito API parameter keys
 * @param preference MFA preference settings using Cognito API keys
 * @returns Updated preference
 */
export async function setMFAPreference(
  preference: {
    [key in MFA_METHOD_ID]?: MFAPreferenceValue;
  }
): Promise<void> {
  // Filter out DISABLED values as Cognito rejects them
  const cognitoPreference: Record<string, 'ENABLED' | 'PREFERRED'> = {};

  for (const [methodId, value] of Object.entries(preference)) {
    if (value && value !== 'DISABLED') {
      // methodId is already the Cognito API parameter key (sms, totp, email)
      cognitoPreference[methodId] = value;
    }
  }

  return await updateMFAPreference(cognitoPreference);
}

/**
 * Set up TOTP for the current user
 * @returns TOTP setup details with QR code info
 */
export async function setupTOTP(): Promise<SetUpTOTPOutput> {
  return await setUpTOTPAmplify();
}

/**
 * Verify TOTP setup with a code from authenticator app
 * @param code Code from authenticator app
 * @returns Verification result
 */
export async function verifyTOTPSetup(code: string): Promise<void> {
  return await verifyTOTPSetupAmplify({ code });
}

/**
 * Disable specific MFA method for current user using simplified method IDs
 * @param method The MFA method to disable ('sms' | 'totp' | 'email')
 */
export async function disableMFAMethod(method: MFA_METHOD_ID): Promise<void> {
  const currentPreference = await getMFAPreference();

  // Build the UpdateMFAPreferenceInput object using the OpenAPI type as the source of truth
  const updatePreferenceInput: UpdateMFAPreferenceInput = {
    sms:
      currentPreference.enabled?.includes('SMS') && method !== 'sms'
        ? 'ENABLED'
        : method === 'sms'
          ? 'DISABLED'
          : undefined,
    totp:
      currentPreference.enabled?.includes('TOTP') && method !== 'totp'
        ? 'ENABLED'
        : method === 'totp'
          ? 'DISABLED'
          : undefined,
    email:
      currentPreference.enabled?.includes('EMAIL') && method !== 'email'
        ? 'ENABLED'
        : method === 'email'
          ? 'DISABLED'
          : undefined,
  };

  // Remove undefined keys (Cognito does not accept them)
  for (const k of Object.keys(updatePreferenceInput)) {
    if (updatePreferenceInput[k as keyof UpdateMFAPreferenceInput] === undefined) {
      delete updatePreferenceInput[k as keyof UpdateMFAPreferenceInput];
    }
  }

  return await updateMFAPreference(updatePreferenceInput);
}

/**
 * Update user's password while authenticated
 * @param oldPassword Current password for verification
 * @param newPassword New password to set
 * @returns Promise that resolves when password is updated
 */
export async function updatePassword(oldPassword: string, newPassword: string): Promise<void> {
  const input: UpdatePasswordInput = {
    oldPassword,
    newPassword,
  };

  return await updatePasswordAmplify(input);
}

// Legacy class wrapper for backwards compatibility
export const MFAService = {
  getMFAPreference,
  setMFAPreference,
  setupTOTP,
  verifyTOTPSetup,
  disableMFAMethod,
};
